import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { PlanService, Plan, PlanResponse } from '../services/plan.service';
import { StandortService, Standort } from '../services/standort.service';
import { DokumenteService } from '../services/dokumente.service';
import { ObjektStrukturService, RoomIndikator } from '../services/objekt-struktur.service';
import { environment } from '../../environments/environment';
import { RoomIndicator } from '../components/interactive-plan/models/plan-marker.interface';

@Component({
  templateUrl: './plaene.component.html',
  styleUrls: ['./plaene.component.scss']
})
export class PlaeneComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Standort selection
  standorte: Standort[] = [];
  selectedStandortId: string | null = null;
  selectedStandort: Standort | null = null;
  
  // Plan data
  plans: Plan[] = [];
  selectedPlan: Plan | null = null;
  
  // Plan image
  planImageUrl: string | null = null;
  isLoadingPlanImage = false;
  
  // Loading states
  isLoadingStandorte = false;
  isLoadingPlans = false;
  isLoadingIndicators = false;
  isDownloadingPDF = false;
  
  // Room indicators for interactive plan
  roomIndicators: RoomIndicator[] = [];

  constructor(
    private snackBar: MatSnackBar,
    private planService: PlanService,
    private standortService: StandortService,
    private dokumenteService: DokumenteService,
    private objektStrukturService: ObjektStrukturService
  ) {}

  ngOnInit(): void {
    this.loadStandorte();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStandorte(): void {
    this.isLoadingStandorte = true;
    // SECURITY: API filters server-side by kontakt permissions
    // The API endpoint /v5/standort automatically filters to only return standorte
    // that are in the kontakt_standort_berechtigungen table for this kontakt user.
    // The frontend MUST NOT filter this list - it only displays what the API returns.
    this.standortService.getStandorte(1, 10000).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // IMPORTANT: Use API response directly - do NOT filter client-side
        // The API has already filtered to only permitted standorte
        this.standorte = response.data || [];
        this.isLoadingStandorte = false;
        
        // Auto-select first standort if only one
        if (this.standorte.length === 1) {
          this.selectedStandortId = this.standorte[0].id;
          this.selectedStandort = this.standorte[0];
          this.loadPlansForStandort(this.standorte[0].id);
        }
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
        this.snackBar.open('Fehler beim Laden der Standorte', 'Schließen', { duration: 3000 });
        this.isLoadingStandorte = false;
      }
    });
  }

  onStandortChange(): void {
    if (this.selectedStandortId) {
      this.selectedStandort = this.standorte.find(s => s.id === this.selectedStandortId) || null;
      this.selectedPlan = null;
      this.planImageUrl = null;
      this.roomIndicators = [];
      this.loadPlansForStandort(this.selectedStandortId);
    } else {
      this.plans = [];
      this.selectedPlan = null;
      this.planImageUrl = null;
    }
  }

  loadPlansForStandort(standortId: string): void {
    this.isLoadingPlans = true;
    this.planService.getPlansByStandort(standortId, 1, 100).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: PlanResponse) => {
        // Filter for active plans only
        this.plans = (response.data || []).filter(plan => plan.active || plan.is_active);
        this.isLoadingPlans = false;
        
        // Auto-select if only one plan
        if (this.plans.length === 1) {
          this.selectPlan(this.plans[0]);
        }
      },
      error: (error) => {
        console.error('Error loading plans:', error);
        this.snackBar.open('Fehler beim Laden der Pläne', 'Schließen', { duration: 3000 });
        this.isLoadingPlans = false;
      }
    });
  }

  selectPlan(plan: Plan): void {
    this.selectedPlan = plan;
    this.planImageUrl = null;
    this.roomIndicators = [];
    this.loadPlanImage(plan);
    if (this.selectedStandortId) {
      this.loadRoomIndicators(this.selectedStandortId, plan.sektion_id);
    }
  }

  loadPlanImage(plan: Plan): void {
    if (!plan.upload_id) {
      this.snackBar.open('Kein Bild für diesen Plan verfügbar', 'Schließen', { duration: 3000 });
      return;
    }

    this.isLoadingPlanImage = true;
    this.planImageUrl = null;

    // Get signed URL for plan image
    this.dokumenteService.signDokumentUrl(plan.upload_id, 3600, false).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response.url) {
          // Ensure URL is complete
          const base = environment.apiUrl.replace(/\/v5$/, '');
          this.planImageUrl = `${base}${response.url}`;
        } else {
          this.snackBar.open('Fehler beim Laden des Planbildes', 'Schließen', { duration: 3000 });
        }
        this.isLoadingPlanImage = false;
      },
      error: (error) => {
        console.error('Error loading plan image:', error);
        this.snackBar.open('Fehler beim Laden des Planbildes', 'Schließen', { duration: 3000 });
        this.isLoadingPlanImage = false;
      }
    });
  }

  loadRoomIndicators(standortId: string, sektionId?: string): void {
    this.isLoadingIndicators = true;
    this.objektStrukturService.getRoomIndikatorenByStandort(standortId, 200).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: RoomIndikator[]) => {
        // Handle both array response and paginated response
        let allIndicators: RoomIndikator[] = [];
        if (Array.isArray(response)) {
          allIndicators = response;
        } else if ((response as any).data && Array.isArray((response as any).data)) {
          allIndicators = (response as any).data;
        }
        
        // Filter by sektion (if plan has sektion_id) and only active indicators
        let filtered: RoomIndikator[];
        if (sektionId) {
          filtered = allIndicators.filter(indicator => {
            const matchesSektion = (indicator as any).room?.sektion?.id === sektionId;
            const statusValue = indicator.status as any;
            const isActive = statusValue === 1 || statusValue === true;
            return matchesSektion && isActive;
          });
        } else {
          // If no sektion_id, just filter by active status
          filtered = allIndicators.filter(indicator => {
            const statusValue = indicator.status as any;
            return statusValue === 1 || statusValue === true;
          });
        }
        
        // Convert to RoomIndicator format (matching the interface)
        this.roomIndicators = filtered.map(ri => ({
          id: ri.id,
          name: ri.name,
          current_value: ri.current_value,
          einheit: ri.einheit,
          schwellenwert: parseFloat(ri.schwellenwert) || 0,
          status: ri.status,
          room_id: ri.room_id,
          indikator_id: ri.indikator_id,
          bemerkung: ri.bemerkung,
          qr_code: ri.qr_code,
          mehr_weniger: parseInt(ri.mehr_weniger) || 0,
          indikator_number: ri.indikator_number,
          position_x: ri.position_x,
          position_y: ri.position_y,
          system_id: ri.system_id,
          systemtyp_caption: (ri as any).systemtyp_caption,
          created_at: ri.created_at,
          updated_at: ri.updated_at,
          room: (ri as any).room,
          indikator: (ri as any).indikator
        } as RoomIndicator));
        
        this.isLoadingIndicators = false;
        console.log('Room indicators loaded for interactive plan:', this.roomIndicators.length);
      },
      error: (error) => {
        console.error('Error loading room indicators:', error);
        this.snackBar.open('Fehler beim Laden der Indikatoren', 'Schließen', { duration: 3000 });
        this.isLoadingIndicators = false;
      }
    });
  }

  downloadPlanPDF(): void {
    if (!this.selectedPlan) {
      this.snackBar.open('Kein Plan ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }

    this.isDownloadingPDF = true;

    this.planService.downloadPlanPDF(this.selectedPlan.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob: Blob) => {
        this.isDownloadingPDF = false;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Generate user-friendly filename
        const planName = this.selectedPlan?.bezeichnung || 'Plan';
        const standortName = this.selectedStandort?.name || 'Standort';
        
        // Sanitize names for filename
        const sanitizeForFilename = (str: string): string => {
          return str
            .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '') // Remove special chars except spaces and hyphens
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .substring(0, 50); // Limit length
        };

        const sanitizedPlanName = sanitizeForFilename(planName);
        const sanitizedStandortName = sanitizeForFilename(standortName);
        const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

        link.download = `Plan-${sanitizedPlanName}-${sanitizedStandortName}-${date}.pdf`;
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);

        this.snackBar.open('PDF erfolgreich heruntergeladen', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        this.isDownloadingPDF = false;
        console.error('Error downloading plan PDF:', error);
        this.snackBar.open('Fehler beim Herunterladen des PDFs', 'Schließen', { duration: 3000 });
      }
    });
  }
}

